import type { GraphNode, PageGraph, WikiGraph } from '@/api/client';
import { GRAPH } from '@/config';
import { useT } from '@/i18n/LocaleProvider';

export type GraphPanelProps = {
  error?: string | null;
  isLoading?: boolean;
  pageGraph?: PageGraph | null;
  onClose?: () => void;
  onNavigate?: (slug: string) => void;
};

type Point = {
  x: number;
  y: number;
};

type PositionedNode = GraphNode & Point;

const graphCenter = GRAPH.size / 2;

export function GraphPanel({
  error,
  isLoading = false,
  pageGraph,
  onClose,
  onNavigate,
}: GraphPanelProps) {
  const t = useT();
  const linkCount = pageGraph
    ? `${pageGraph.center.outbound_count} / ${pageGraph.center.inbound_count}`
    : '';

  return (
    <aside
      className="network-panel"
      data-testid="network-panel"
      aria-label={t('graph_relations_title')}
    >
      <div className="panel-header">
        <h2>{t('graph_relations_title')}</h2>
        <span data-testid="link-count">{linkCount}</span>
        {onClose ? (
          <button
            aria-label={t('graph_hide_btn')}
            className="panel-close"
            type="button"
            onClick={onClose}
          >
            ×
          </button>
        ) : null}
      </div>

      <svg
        className="graph"
        viewBox={`0 0 ${GRAPH.size} ${GRAPH.size}`}
        role="img"
        aria-label={t('graph_svg_label')}
        data-testid="graph-svg"
      >
        {pageGraph ? (
          <RadialGraph
            activeNodeID={pageGraph.center.id}
            graph={pageGraph.graph}
            onNavigate={onNavigate}
          />
        ) : null}
      </svg>

      <section className="backlinks">
        <h3>{t('graph_backlinks')}</h3>
        <Backlinks
          backlinks={pageGraph?.backlinks ?? []}
          error={error}
          isLoading={isLoading}
          onNavigate={onNavigate}
        />
      </section>
    </aside>
  );
}

type RadialGraphProps = {
  activeNodeID: string;
  graph: WikiGraph;
  onNavigate?: (slug: string) => void;
};

function RadialGraph({ activeNodeID, graph, onNavigate }: RadialGraphProps) {
  const positionedNodes = layoutRadialNodes(graph);
  const positions = new Map(positionedNodes.map((node) => [node.id, { x: node.x, y: node.y }]));

  return (
    <>
      {graph.edges.map((edge) => {
        const from = positions.get(edge.from);
        const to = positions.get(edge.to);

        if (!from || !to) {
          return null;
        }

        const active = edge.from === activeNodeID || edge.to === activeNodeID;

        return (
          <line
            className="graph-edge"
            data-active={active ? 'true' : undefined}
            key={`${edge.from}->${edge.to}`}
            style={active ? { stroke: 'var(--crimson)', strokeWidth: 2.2 } : undefined}
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
          />
        );
      })}

      {positionedNodes.map((node) => (
        <GraphNodeMarker
          active={node.id === activeNodeID}
          key={node.id}
          node={node}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
}

type GraphNodeMarkerProps = {
  active: boolean;
  node: PositionedNode;
  onNavigate?: (slug: string) => void;
};

function GraphNodeMarker({ active, node, onNavigate }: GraphNodeMarkerProps) {
  const clickable = Boolean(node.page_id && node.slug);
  const missing = isMissingNode(node);
  const className = ['graph-node', active ? 'is-active' : '', missing ? 'is-missing' : '']
    .filter(Boolean)
    .join(' ');
  const href = clickable && node.slug ? `/pages/${encodeURIComponent(node.slug)}` : '';
  const label = node.title.length > 18 ? `${node.title.slice(0, 17)}...` : node.title;
  const navigate = () => {
    if (clickable && node.slug) {
      onNavigate?.(node.slug);
    }
  };

  const marker = (
    <>
      <circle r={active ? 8 : 6} />
      <text x="10" y="4">
        {label}
      </text>
    </>
  );

  if (!clickable) {
    return (
      <g
        className={className}
        data-clickable="false"
        data-href=""
        data-node-id={node.id}
        transform={`translate(${node.x} ${node.y})`}
      >
        {marker}
      </g>
    );
  }

  return (
    <a
      href={href}
      onClick={(event) => {
        event.preventDefault();
        navigate();
      }}
    >
      <g
        className={className}
        data-clickable="true"
        data-href={href}
        data-node-id={node.id}
        transform={`translate(${node.x} ${node.y})`}
      >
        {marker}
      </g>
    </a>
  );
}

type BacklinksProps = {
  backlinks: GraphNode[];
  error?: string | null;
  isLoading: boolean;
  onNavigate?: (slug: string) => void;
};

function Backlinks({ backlinks, error, isLoading, onNavigate }: BacklinksProps) {
  const t = useT();

  if (isLoading) {
    return <div className="empty-note">{t('graph_loading')}</div>;
  }

  if (error) {
    return <div className="empty-note">{error}</div>;
  }

  if (backlinks.length === 0) {
    return <div className="empty-note">{t('graph_empty')}</div>;
  }

  return (
    <ul className="backlink-list">
      {backlinks.map((node) => {
        const clickable = Boolean(node.page_id && node.slug);

        if (!clickable || !node.slug) {
          return (
            <li key={node.id}>
              <span>{node.title}</span>
            </li>
          );
        }

        const slug = node.slug;

        return (
          <li key={node.id}>
            <a
              href={`/pages/${encodeURIComponent(slug)}`}
              onClick={(event) => {
                event.preventDefault();
                onNavigate?.(slug);
              }}
            >
              {node.title}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function layoutRadialNodes(graph: WikiGraph): PositionedNode[] {
  const knownNodes = graph.nodes.filter((node) => !isMissingNode(node));
  const missingNodes = graph.nodes.filter(isMissingNode);
  const positions = new Map<string, Point>();

  knownNodes.forEach((node, index) => {
    positions.set(node.id, polarPoint(GRAPH.knownRadius, index, knownNodes.length));
  });

  missingNodes.forEach((node, index) => {
    positions.set(node.id, polarPoint(GRAPH.missingRadius, index, missingNodes.length || 1));
  });

  return graph.nodes.map((node) => {
    const point = positions.get(node.id) ?? { x: graphCenter, y: graphCenter };

    return {
      ...node,
      ...point,
    };
  });
}

function polarPoint(radius: number, index: number, total: number): Point {
  const angle = -Math.PI / 2 + (Math.PI * 2 * index) / Math.max(1, total);

  return {
    x: graphCenter + Math.cos(angle) * radius,
    y: graphCenter + Math.sin(angle) * radius,
  };
}

function isMissingNode(node: GraphNode): boolean {
  return node.missing || node.stub;
}
